/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { changePassword } from 'services/userServices';
import { Button, Icon, useToast } from '@coreai/component-library';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from 'LanguageContext';
import { useUserDataService } from 'services/useUserDataService';

const UpdatePasswordForm = () => {
  const theme = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { t, setLanguage } = useLanguage();
  const [isInvalidPassword, setIsInvalidPassword] = useState(false);
  const { userData } = useUserDataService();

  useEffect(() => {
    if (userData?.localeCode) {
      setLanguage(userData.localeCode);
    }
  }, [userData]);

  const containerStyle = css`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    padding: 1rem;
  `;

  const formStyle = css`
    background: white;
    padding: 2.5rem;
    border-radius: 0.5rem;
    box-shadow: 0px 4px 24px 0px #0000001A;
    width: 100%;
    max-width: 450px;
  `;

  const headingStyle = css`
    font-size: 30px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0%;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  `;

  const descriptionStyle = css`
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    color: #3d5360;
    text-align: left;
    margin: 20px 0;

    @media (max-width: 768px) {
      margin: 12px 0;
    }
  `;

  const labelStyle = css`
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  `;

  const inputStyle = css`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #acb8bd;
    border-radius: 0.5rem;
    margin-bottom: 1.25rem;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}33;
    }
  `;

  const confirmPasswordStyle = css`
    margin-bottom: 4px;
  `;

  const buttonStyle = css`
    width: 100%;
    font-size: 16px;
    background-color: ${theme.colors.primary};
    color: white;
    padding: 0.5rem;
    border: none;
    border-radius: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  `;

useEffect(() => {
  const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_\-=`~+\[\]{};'"\\|,.<>\/?]).{8,20}$/;
  setIsInvalidPassword(!passwordRegex.test(newPassword));
}, [newPassword]);

const wrapperStyle = css`
  display: flex;
  gap: 4px;
  align-items: flex-start;
  padding: 0.5rem 0;
`;

const passwordValidStyle = css`
  font-size: 12px;
  color: ${
    newPassword?.length > 0
      ? isInvalidPassword
        ? theme.colors.red
        : theme.colors.primary
      : theme.colors.textMedium
  };
  margin-top: 0px;
  line-height: 20px;
`;

const iconStyle = css`
  width: 16px;
  height: 16px;
  color: ${
      newPassword?.length > 0
        ? isInvalidPassword
          ? theme.colors.red
          : theme.colors.primary
        : theme.colors.textMedium
    };
`;

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    const formData = {
      "current_password": currentPassword,
      "new_password": newPassword
    };

    changePassword(formData).then((result: any) => {
      if(result.success){
        toast({
        variant: 'success',
        title: result?.message,
        });
        // navigate(`/success?page=update_password`);
        window.location.href = `/success?page=update_password`;
      } else if (result?.message) {
        toast({
          variant: 'error',
          title: result?.message,
          });
      }
    }).catch((error: any) => {
      console.log({error},'updateUserConsent');
      toast({
      variant: 'error',
      title: error?.response?.data?.message,
      });
    });
  };

  return (
    <div css={containerStyle}>
      <form css={formStyle} onSubmit={handleSubmit}>
        <h2 css={headingStyle}>{t('update_password_title')}</h2>
        <p css={descriptionStyle}>
          {t('update_password_desc')}
        </p>

        <div>
          <label css={labelStyle}>{t('update_password_current')}</label>
          <input
            type="password"
            placeholder={t('update_password_enter_current')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            css={inputStyle}
            required
          />
        </div>

        <div>
          <label css={labelStyle}>{t('reset_password_new')}</label>
          <input
            type="password"
            placeholder={t('update_password_enter_new')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            css={[inputStyle, confirmPasswordStyle]}
            required
          />
        </div>
        <div css={wrapperStyle}>
          <Icon name={`${isInvalidPassword ? "info-circle" : "check-circle"}`} size="sm" css={iconStyle}/>
          <p css={passwordValidStyle}>{t('password_policy')}</p>
        </div>

        <div>
          <label css={labelStyle}>{t('reset_password_confirm')}</label>
          <input
            type="password"
            placeholder={t('update_password_enter_confirm')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            css={inputStyle}
            required
          />
        </div>
        <Button type="submit" css={buttonStyle} disabled={ (newPassword !== confirmPassword) ||  currentPassword ==="" || newPassword ==="" || confirmPassword === ""}>
          {t('update_password_btn')}
        </Button>
      </form>
    </div>
  );
};

export default UpdatePasswordForm;